import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Modal, Select, Spin, Table, Tag, Typography, message } from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import api from './api';
import useIsMobile from '../../../util/useIsMobile';

const PARTY_NUMBERS = [1, 2, 3, 4, 5, 6];

const ROLE_LABEL = {
  DEALER: '딜러',
  SUPPORT: '서포터',
  UNKNOWN: '미확인',
};

const ROLE_COLOR = {
  DEALER: '#cf1322',
  SUPPORT: '#389e0d',
  UNKNOWN: '#888888',
};

const fmtLevel = (v) => (v == null ? '-' : Number(v).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtPower = (v) => (v == null ? '-' : Number(v).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const pendingKeyOf = (partyNumber, memberId) => `${partyNumber}-${memberId}`;

function CharacterOptionLabel({ character }) {
  const classText = character.class_name || ROLE_LABEL[character.role] || '미확인';
  return (
    <span>
      {character.character_name}
      <span style={{ color: ROLE_COLOR[character.role] || ROLE_COLOR.UNKNOWN }}>({classText})</span>
    </span>
  );
}

function CellContent({ character }) {
  if (!character) {
    return <Typography.Text type="secondary" style={{ fontSize: 12 }}>미배치</Typography.Text>;
  }
  return (
    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
      아이템레벨 {fmtLevel(character.current_item_level)} · 최고전투력 {fmtPower(character.max_combat_power)}
    </div>
  );
}

function PartyMobileCard({
  partyNumber,
  roster,
  recommendedRaids,
  displayedCharacterIdOf,
  characterByIdOf,
  charactersByMemberId,
  pendingChanges,
  onSelectChange,
}) {
  return (
    <Card size="small" title={`파티 ${partyNumber}`} style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
      {recommendedRaids.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {recommendedRaids.map((r) => (
            <Tag color="blue" key={r.raid_name}>{r.raid_name} {r.tier_name}</Tag>
          ))}
        </div>
      )}
      {roster.map((member) => {
        const characterId = displayedCharacterIdOf(partyNumber, member.member_id);
        const character = characterByIdOf(member.member_id, characterId);
        const key = pendingKeyOf(partyNumber, member.member_id);
        const isDirty = Object.prototype.hasOwnProperty.call(pendingChanges, key);
        const options = (charactersByMemberId[member.member_id] || []).map((c) => ({
          value: c.character_id,
          label: <CharacterOptionLabel character={c} />,
        }));
        return (
          <div
            key={member.member_id}
            style={{
              marginBottom: 10,
              borderLeft: isDirty ? '3px solid #1677ff' : '3px solid transparent',
              paddingLeft: 8,
            }}
          >
            <Typography.Text style={{ fontSize: 12, color: '#888' }}>{member.member_name}</Typography.Text>
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder="캐릭터 선택"
              value={characterId ?? undefined}
              options={options}
              onChange={(value) => onSelectChange(partyNumber, member.member_id, value)}
            />
            <CellContent character={character} />
          </div>
        );
      })}
    </Card>
  );
}

export default function LostArkPartyPage() {
  const [roster, setRoster] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const isMobile = useIsMobile();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rosterRes, assignmentsRes] = await Promise.all([
        api.get('/roster'),
        api.get('/assignments'),
      ]);
      setRoster(rosterRes.data);
      setAssignments(assignmentsRes.data.parties);
      setPendingChanges({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const charactersByMemberId = useMemo(() => {
    const map = {};
    roster.forEach((m) => { map[m.member_id] = m.characters; });
    return map;
  }, [roster]);

  const cellOf = useCallback((partyNumber, memberId) => {
    const party = assignments.find((p) => p.party_number === partyNumber);
    if (!party) return undefined;
    return party.assignments.find((a) => a.member_id === memberId);
  }, [assignments]);

  const savedCharacterIdOf = useCallback((partyNumber, memberId) => {
    const cell = cellOf(partyNumber, memberId);
    return cell?.character ? cell.character.character_id : null;
  }, [cellOf]);

  const displayedCharacterIdOf = useCallback((partyNumber, memberId) => {
    const key = pendingKeyOf(partyNumber, memberId);
    return Object.prototype.hasOwnProperty.call(pendingChanges, key)
      ? pendingChanges[key]
      : savedCharacterIdOf(partyNumber, memberId);
  }, [pendingChanges, savedCharacterIdOf]);

  const characterByIdOf = useCallback((memberId, characterId) => {
    if (characterId == null) return null;
    return (charactersByMemberId[memberId] || []).find((c) => c.character_id === characterId) || null;
  }, [charactersByMemberId]);

  const recommendedRaidsOf = useCallback((partyNumber) => {
    const party = assignments.find((p) => p.party_number === partyNumber);
    return party ? party.recommended_raids : [];
  }, [assignments]);

  const pendingCount = Object.keys(pendingChanges).length;

  const handleSelectChange = useCallback((partyNumber, memberId, characterId) => {
    const key = pendingKeyOf(partyNumber, memberId);
    const saved = savedCharacterIdOf(partyNumber, memberId);
    const nextValue = characterId ?? null;
    setPendingChanges((prev) => {
      const next = { ...prev };
      if (nextValue === saved) {
        delete next[key];
      } else {
        next[key] = nextValue;
      }
      return next;
    });
  }, [savedCharacterIdOf]);

  const handleSave = async () => {
    const entries = Object.entries(pendingChanges);
    if (entries.length === 0) return;
    setSaving(true);
    try {
      const assignmentsPayload = entries.map(([key, characterId]) => {
        const [partyNumber, memberId] = key.split('-').map(Number);
        return { party_number: partyNumber, member_id: memberId, character_id: characterId };
      });
      const res = await api.put('/assignments', { assignments: assignmentsPayload });
      setAssignments(res.data.parties);
      setPendingChanges({});
      message.success(`${entries.length}건 저장했어요`);
    } catch (e) {
      message.error('저장에 실패했어요');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    const doRefresh = async () => {
      setRefreshing(true);
      try {
        const res = await api.post('/refresh');
        const { total_count, success_count, failed_characters } = res.data;
        if (failed_characters && failed_characters.length > 0) {
          message.warning(`${total_count}개 중 ${success_count}개 갱신 성공. 실패: ${failed_characters.join(', ')}`);
        } else {
          message.success(`전체 ${total_count}개 캐릭터 갱신 완료`);
        }
        await fetchAll();
      } catch (e) {
        message.error('갱신에 실패했어요');
      } finally {
        setRefreshing(false);
      }
    };

    if (pendingCount > 0) {
      Modal.confirm({
        title: '저장하지 않은 변경사항이 있어요',
        content: `배치 변경 ${pendingCount}건이 저장되지 않았습니다. 갱신하면 사라집니다. 계속할까요?`,
        okText: '갱신',
        cancelText: '취소',
        onOk: doRefresh,
      });
      return;
    }
    doRefresh();
  };

  const columns = useMemo(() => {
    const memberColumn = {
      title: '인원',
      dataIndex: 'member_name',
      key: 'member',
      fixed: 'left',
      width: 90,
    };

    const partyColumns = PARTY_NUMBERS.map((partyNumber) => ({
      title: `파티 ${partyNumber}`,
      key: `party-${partyNumber}`,
      width: 220,
      render: (_, record) => {
        const characterId = displayedCharacterIdOf(partyNumber, record.member_id);
        const character = characterByIdOf(record.member_id, characterId);
        const key = pendingKeyOf(partyNumber, record.member_id);
        const isDirty = Object.prototype.hasOwnProperty.call(pendingChanges, key);
        const options = (charactersByMemberId[record.member_id] || []).map((c) => ({
          value: c.character_id,
          label: <CharacterOptionLabel character={c} />,
        }));
        return (
          <div style={isDirty ? { borderLeft: '3px solid #1677ff', paddingLeft: 6, marginLeft: -9 } : undefined}>
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder="캐릭터 선택"
              value={characterId ?? undefined}
              options={options}
              onChange={(value) => handleSelectChange(partyNumber, record.member_id, value)}
            />
            <CellContent character={character} />
          </div>
        );
      },
    }));

    return [memberColumn, ...partyColumns];
  }, [charactersByMemberId, displayedCharacterIdOf, characterByIdOf, pendingChanges, handleSelectChange]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, rowGap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>로스트아크 파티 편성</Typography.Title>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {pendingCount > 0 && <Tag color="blue">변경사항 {pendingCount}건</Tag>}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={pendingCount === 0}
            onClick={handleSave}
          >
            저장
          </Button>
          <Button icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefresh}>
            전체 캐릭터 갱신
          </Button>
        </div>
      </div>

      {roster.length === 0 ? (
        <Empty description="등록된 로스터가 없어요" />
      ) : isMobile ? (
        <div>
          {PARTY_NUMBERS.map((partyNumber) => (
            <PartyMobileCard
              key={partyNumber}
              partyNumber={partyNumber}
              roster={roster}
              recommendedRaids={recommendedRaidsOf(partyNumber)}
              displayedCharacterIdOf={displayedCharacterIdOf}
              characterByIdOf={characterByIdOf}
              charactersByMemberId={charactersByMemberId}
              pendingChanges={pendingChanges}
              onSelectChange={handleSelectChange}
            />
          ))}
        </div>
      ) : (
        <Table
          rowKey="member_id"
          dataSource={roster}
          columns={columns}
          pagination={false}
          scroll={{ x: 'max-content' }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>추천 레이드</Table.Summary.Cell>
                {PARTY_NUMBERS.map((partyNumber, idx) => (
                  <Table.Summary.Cell index={idx + 1} key={partyNumber}>
                    {recommendedRaidsOf(partyNumber).length === 0 ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>-</Typography.Text>
                    ) : (
                      recommendedRaidsOf(partyNumber).map((r) => (
                        <Tag color="blue" key={r.raid_name}>{r.raid_name} {r.tier_name}</Tag>
                      ))
                    )}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}
    </div>
  );
}
